import { Bar } from "recharts";
import { colorForIndex, TOTAL_COLOR } from "./colors";

const TOTAL_KEY = "total";

/**
 * Bars for one hero BarChart.
 *
 * The hero stacks THREE charts (fixed y-axis, background grid, interactive
 * plot) and this runs for all three, so the drill handler is opt-in per
 * instantiation: only the interactive plot passes `onDrill`. Attaching it to
 * the hidden instantiations would make one click fire two or three times, or
 * fire from an invisible bar (R09 item 5 brief risk item 7).
 *
 * The handler closes over the entity id at map time on purpose. recharts
 * 2.15.4 hands a Bar click the composed rectangle entry, which does NOT carry
 * dataKey, so reading the id off the event payload would not work (brief 3.2
 * item 5). Consolidated mode has a single Total bar and no per-entity id to
 * drill into, so it never gets a handler.
 *
 * This is a render helper, not a component: recharts reads the element types
 * of BarChart's direct children, so wrapping the Bars would break the chart.
 * It lives in its own module so the click surface is unit-testable.
 */
export const renderHeroBarSeries = ({
  consolidated,
  entities,
  entityColorById,
  hidden = false,
  onDrill = null,
}) => {
  if (consolidated) {
    return (
      <Bar
        key={hidden ? `hidden-${TOTAL_KEY}` : TOTAL_KEY}
        dataKey={TOTAL_KEY}
        fill={hidden ? "transparent" : TOTAL_COLOR}
        stroke={hidden ? "transparent" : undefined}
        // Never animated: the stale-while-revalidate swap replaces data in
        // mounted charts, and a growth animation on every swap re-introduces
        // exactly the motion the veil exists to remove (brief section 3
        // point 7).
        isAnimationActive={false}
        radius={[2, 2, 0, 0]}
        barSize={20}
      />
    );
  }

  const drillable = !hidden && typeof onDrill === "function";

  return entities.map((id, idx) => (
    <Bar
      key={hidden ? `hidden-${id}` : String(id)}
      dataKey={String(id)}
      fill={
        hidden
          ? "transparent"
          : (entityColorById?.get(String(id)) ?? colorForIndex(idx))
      }
      stroke={hidden ? "transparent" : undefined}
      fillOpacity={hidden ? 0 : undefined}
      isAnimationActive={false}
      radius={[2, 2, 0, 0]}
      barSize={20}
      className={drillable ? "cursor-pointer" : undefined}
      onClick={drillable ? () => onDrill(String(id)) : undefined}
    />
  ));
};
